import { FC, memo } from 'react';
import { Text, TypographyHorizontalAlign } from '@picsart/text';
import { Radius, Spacings, Texts, Typography } from '@picsart/cascade';
import Grid from '@picsart/grid/Grid';
import { useTranslator } from '@picsart/rc/services/localization';
import IconNone from '@picsart/icons/IconNone';
import IconCrossCircleFilled from '@picsart/icons/IconCrossCircleFilled';
import ImageFrame from '@picsart/imageframe/ImageFrame';
import Image from '@picsart/image/Image';
import View from '@picsart/view/View';
import { FileRejection } from 'react-dropzone';

import { getUserWatermarks } from 'store/batch/selectors';
import useMemoSelector from 'hooks/useMemoSelector';
import UploadButton from 'pages/Batch/components/UploadButton';
import { IUserWatermark } from 'types/store';

import useStyles from '../styles';
import testIds from '../testIds';

type WatermarkLogosProps = {
  selectedLogoId: string | null;
  handleLogoRemove: () => void;
  handleLogoDelete: (el: IUserWatermark) => void;
  handleLogoSelect: (el: IUserWatermark) => void;
  handleLogoUpload: (files: File[], rejectedFiles: FileRejection[]) => void;
  handleLogoUploadCancel: () => void;
}

const WatermarkLogos: FC<WatermarkLogosProps> = ({ selectedLogoId, handleLogoRemove, handleLogoDelete, handleLogoSelect, handleLogoUpload, handleLogoUploadCancel }) => {
  const classes = useStyles();
  const translate = useTranslator();
  const userWatermarks = useMemoSelector(getUserWatermarks);

  return (
    <View
      flexDirection="column"
      gap={Spacings.s16}
      paddingBlockEnd={Spacings.s16}
    >
      <Text size={Typography.t4} color={Texts.base} align={TypographyHorizontalAlign.Left}>{translate('Your watermarks')}</Text>

      <Grid columns="repeat(4, 1fr)" gap={Spacings.s6}>
        <ImageFrame width={75} height={75} borderRadius={Radius.r12} onClick={handleLogoRemove} dataTestId={testIds.delete}>
          <IconNone className={classes.iconNone} />
        </ImageFrame>

        {userWatermarks.map(el => (
          <ImageFrame
            key={el.id}
            width={75}
            height={75}
            borderRadius={Radius.r12}
            isActive={selectedLogoId === el.id}
            onClick={() => handleLogoSelect(el)}
          >
            <View>
              <IconCrossCircleFilled
                data-testid={testIds.deleteLogo}
                className={classes.crossIcon}
                onClick={e => {
                  e.stopPropagation();
                  handleLogoDelete(el);
                }}
              />
              <Image
                className={classes.iconNone}
                src={el.url}
                alt={translate('Logo Image')}
              />
            </View>
          </ImageFrame>
        ))}

        <ImageFrame width={75} height={75} borderRadius={Radius.r12}>
          <View
            height="100%"
          >
            <UploadButton
              title=""
              allowMultiple={false}
              useIcon
              onSelect={handleLogoUpload}
              onCancel={handleLogoUploadCancel}
            />
          </View>
        </ImageFrame>
      </Grid>
    </View>
  );
};

export default memo(WatermarkLogos);
